/**
 * Environment configuration validation and management
 * Ensures all required environment variables are present and valid
 */

import { logger } from './logger';

/**
 * Interface defining all environment configuration
 */
interface EnvironmentConfig {
  /** Base URL for API endpoints */
  apiUrl: string;
  /** Application environment (development, staging, production) */
  environment: 'development' | 'staging' | 'production';
  /** Whether to enable debug mode */
  debugMode: boolean;
  /** Application version */
  version: string;
  /** Whether this is a production build */
  isProduction: boolean;
}

/**
 * Validates a URL string
 * @param url - The URL to validate
 * @param name - The name of the environment variable for error reporting
 * @returns The validated URL
 * @throws Error if URL is invalid
 */
function validateUrl(url: string, name: string): string {
  try {
    const urlObj = new URL(url);
    
    // Ensure HTTPS in production
    if (process.env.NODE_ENV === 'production' && urlObj.protocol !== 'https:') {
      throw new Error(`${name} must use HTTPS in production`);
    }
    
    // Validate allowed protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      throw new Error(`${name} must use HTTP or HTTPS protocol`);
    }
    
    return url;
  } catch (error) {
    throw new Error(`Invalid URL for ${name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validates environment value against allowed options
 * @param value - The value to validate
 * @param allowed - Array of allowed values
 * @param name - The name of the environment variable
 * @returns The validated value
 * @throws Error if value is not allowed
 */
function validateEnvironment(
  value: string, 
  allowed: string[], 
  name: string
): string {
  if (!allowed.includes(value)) {
    throw new Error(`Invalid ${name}: ${value}. Must be one of: ${allowed.join(', ')}`);
  }
  return value;
}

/**
 * Loads and validates environment configuration
 * @returns Validated environment configuration
 * @throws Error if any required environment variable is missing or invalid
 */
function loadEnvironmentConfig(): EnvironmentConfig {
  const errors: string[] = [];
  
  try {
    // Get API URL with fallback
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const validatedApiUrl = validateUrl(apiUrl, 'REACT_APP_API_URL');
    
    // Get environment
    const nodeEnv = process.env.NODE_ENV || 'development';
    const validatedEnvironment = validateEnvironment(
      nodeEnv, 
      ['development', 'staging', 'production'], 
      'NODE_ENV'
    ) as 'development' | 'staging' | 'production';
    
    // Debug mode
    const debugMode = process.env.REACT_APP_DEBUG === 'true' || nodeEnv === 'development';
    
    // Version from package.json (injected as REACT_APP_VERSION by the build/start
    // script) or default. Keep the fallback in sync with package.json.
    const version = process.env.REACT_APP_VERSION || '2.0.0';
    
    // Production flag
    const isProduction = nodeEnv === 'production';
    
    const config: EnvironmentConfig = {
      apiUrl: validatedApiUrl,
      environment: validatedEnvironment,
      debugMode,
      version,
      isProduction
    };
    
    // Log configuration (excluding sensitive data)
    logger.info('Environment configuration loaded', {
      environment: config.environment,
      debugMode: config.debugMode,
      version: config.version,
      apiUrlHost: new URL(config.apiUrl).host // Log host only for security
    });
    
    return config;
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    errors.push(errorMessage);
    logger.error('Environment configuration validation failed', error);
    throw new Error(`Environment validation failed: ${errors.join(', ')}`);
  }
}

/**
 * Validates that required environment variables are set
 * @param required - Array of required environment variable names
 * @throws Error if any required variables are missing
 */
function validateRequiredEnvVars(required: string[]): void {
  const missing = required.filter(varName => !process.env[varName]);
  
  if (missing.length > 0) {
    const error = `Missing required environment variables: ${missing.join(', ')}`;
    logger.error('Required environment variables missing', new Error(error), { missing });
    throw new Error(error);
  }
}

/**
 * Global environment configuration instance
 */
export const env = loadEnvironmentConfig();

/**
 * Validates environment on app startup
 * Call this early in your app initialization
 */
export function initializeEnvironment(): void {
  try {
    // Validate required variables for different environments
    const commonRequired = ['NODE_ENV'];
    
    if (env.isProduction) {
      commonRequired.push('REACT_APP_API_URL');
    }
    
    validateRequiredEnvVars(commonRequired);
    
    // Additional production checks
    if (env.isProduction) {
      if (env.debugMode) {
        logger.warn('Debug mode is enabled in production - consider disabling');
      }
      
      if (!env.apiUrl.startsWith('https://')) {
        logger.warn('API URL is not HTTPS in production - this is a security risk');
      }
    }
    
    logger.info('Environment validation completed successfully');
    
  } catch (error) {
    logger.error('Environment initialization failed', error);
    throw error;
  }
}

/**
 * Gets a validated environment variable with type checking
 * @param key - Environment variable key
 * @param defaultValue - Optional default value
 * @param validator - Optional custom validator function
 * @returns The environment variable value
 */
export function getEnvVar<T = string>(
  key: string, 
  defaultValue?: T,
  validator?: (value: string) => T
): T {
  const value = process.env[key];
  
  if (value === undefined) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Environment variable ${key} is required but not set`);
  }
  
  if (validator) {
    try {
      return validator(value);
    } catch (error) {
      throw new Error(`Invalid value for ${key}: ${error instanceof Error ? error.message : 'Validation failed'}`);
    }
  }
  
  return value as unknown as T;
}

/**
 * Checks if we're running in development mode
 */
export const isDevelopment = env.environment === 'development';

/**
 * Checks if we're running in production mode
 */
export const isProduction = env.isProduction;

/**
 * Checks if debug mode is enabled
 */
export const isDebugMode = env.debugMode;

export default env;